import { useState, useEffect, useRef } from "react";
import { searchSources } from "../api.js";
import { getFaviconUrl } from "../utils/favicon.js";
import styles from "../styles/components/SourceSearch.module.css";

interface Source {
  id: string;
  title: string;
  url: string;
  thumbnails: boolean;
}

interface Props {
  excludeIds?: string[];
  onAdd: (source: Source) => void;
}

export function SourceSearch({ excludeIds = [], onAdd }: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Source[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const sources = await searchSources(query);
        setResults(sources.filter((s) => !excludeIds.includes(s.id)));
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, excludeIds.join(",")]);

  return (
    <div className={styles.container}>
      <input
        type="search"
        className={styles.input}
        placeholder="Search sources…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
      />
      {loading && <p className={styles.hint}>Searching…</p>}
      {!loading && results.length === 0 && query.length > 0 && (
        <p className={styles.hint}>No sources found.</p>
      )}
      <ul className={styles.list}>
        {results.map((source) => (
          <li key={source.id} className={styles.item}>
            <img
              src={getFaviconUrl(new URL(source.url).hostname)}
              className={styles.favicon}
              width={14}
              height={14}
              alt=""
              aria-hidden="true"
            />
            <span className={styles.itemTitle}>{source.title}</span>
            <button
              type="button"
              className={styles.addBtn}
              onClick={() => onAdd(source)}
            >
              Add
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
